import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { LabelComponent } from '../../../shared/components/form/label/label.component';
import { SecurityService } from '../../../services/security/security.service';
import { UsuariosService } from '../../../services/usuario/usuarios.service';
import { Usuario } from '../../../shared/models/usuario.model';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-usuario-edit',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    MultiSelectModule,
    ToggleSwitchModule,
    ButtonModule,
    ComponentCardComponent,
    LabelComponent
  ],
  templateUrl: './usuario-edit.component.html',
  styleUrls: ['./usuario-edit.component.scss']
})
export class UsuarioEditComponent implements OnInit {
  usuarioForm!: FormGroup;
  formCambioPassword!: FormGroup;
  formForzarPassword!: FormGroup;
  loading = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  usuarioId: string | null = null;
  
  // Signals para mostrar/ocultar contraseñas
  showPassword = signal<boolean>(false);
  showConfirmPassword = signal<boolean>(false);
  showOldPassword = signal<boolean>(false);
  showNewPassword = signal<boolean>(false);
  showNewRetypedPassword = signal<boolean>(false);
  showForceNewPassword = signal<boolean>(false);
  showForceRetypedPassword = signal<boolean>(false);

  // Roles disponibles para el sistema
  rolesOptions = [
    { label: 'Agente de Publicidad', value: 'ROLE_AGENTE_PUBLICIDAD' },
    { label: 'Admin de Publicidad', value: 'ROLE_ADMIN_PUBLICIDAD' },
    { label: 'Compaginador', value: 'ROLE_COMPAGINADOR' },
    { label: 'Agente de Facturación', value: 'ROLE_AGENTE_FACTURACION' },
    { label: 'Facturación Auditoría', value: 'ROLE_FACTURACION_AUDITORIA' },
    { label: 'Super Admin', value: 'ROLE_SUPER_ADMIN' }
  ];

  private securityService = inject(SecurityService);
  private usuariosService = inject(UsuariosService);

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService // Usa el MessageService global del app-layout
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.initPasswordForms();
    this.loadUsuario();
  }

  initForm(): void {
    this.usuarioForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      nombre: ['', Validators.required],
      apellido: ['', Validators.required],
      phone: [''],
      roles: [[], Validators.required],
      enabled: [true],
      password: [''],
      confirmPassword: ['']
    });
  }

  initPasswordForms(): void {
    // Formulario para cambio de contraseña (requiere contraseña actual)
    this.formCambioPassword = this.fb.group({
      oldPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(7)]],
      newRetypedPassword: ['', [Validators.required, Validators.minLength(7)]]
    }, { validators: this.passwordsMatch('newPassword', 'newRetypedPassword') });

    // Formulario para forzar contraseña (solo admin, no requiere contraseña actual)
    this.formForzarPassword = this.fb.group({
      forceNewPassword: ['', [Validators.required, Validators.minLength(7)]],
      forceRetypedPassword: ['', [Validators.required, Validators.minLength(7)]]
    }, { validators: this.passwordsMatch('forceNewPassword', 'forceRetypedPassword') });
  }

  passwordsMatch(campo1: string, campo2: string) {
    return (group: FormGroup) => {
      const pass1 = group.controls[campo1].value;
      const pass2 = group.controls[campo2].value;

      if (pass1 === pass2) {
        return null;
      }

      return { passwordsMismatch: true };
    };
  }

  /**
   * Filtra y valida los roles, eliminando valores inválidos y asegurando que solo existan roles válidos
   */
  private filtrarRolesValidos(roles: any[]): string[] {
    if (!roles || !Array.isArray(roles)) {
      return [];
    }

    // Obtener valores válidos de rolesOptions
    const rolesValidosValues = this.rolesOptions.map(opt => opt.value);
    
    return roles
      .filter(role => {
        // Eliminar null, undefined, vacíos
        if (role === null || role === undefined || role === '') return false;
        // Eliminar string 'null'
        const roleStr = String(role).trim().toLowerCase();
        if (roleStr === 'null' || roleStr === '') return false;
        // Solo incluir roles que existen en las opciones disponibles
        return rolesValidosValues.includes(String(role).trim());
      })
      .map(role => String(role).trim())
      .filter((role, index, self) => self.indexOf(role) === index); // Eliminar duplicados
  }

  loadUsuario(): void {
    this.usuarioId = this.route.snapshot.paramMap.get('id');
    
    if (this.usuarioId && this.usuarioId !== 'nuevo') {
      this.isEditMode.set(true);
      this.loading.set(true);
      
      this.usuariosService.getUsuarioById(this.usuarioId).subscribe({
        next: (usuario: Usuario | undefined) => {
          this.loading.set(false);
          if (usuario) {
            // Filtrar y validar roles usando el método helper
            const rolesValidos = this.filtrarRolesValidos(usuario.roles || []);
            
            this.usuarioForm.patchValue({
              email: usuario.email,
              nombre: usuario.nombre || usuario.name,
              apellido: usuario.apellido || usuario.lastName,
              phone: usuario.phone,
              roles: rolesValidos,
              enabled: usuario.enabled
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Usuario no encontrado',
              life: 3000
            });
            this.router.navigate(['/usuarios']);
          }
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al cargar usuario:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los datos del usuario',
            life: 3000
          });
        }
      });
    }
  }

  onSubmit(): void {
    if (this.usuarioForm.invalid) {
      this.markFormGroupTouched(this.usuarioForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos requeridos',
        life: 3000
      });
      return;
    }

    const formValue = this.usuarioForm.value;
    
    // Filtrar y validar roles usando el método helper
    const rolesValidos = this.filtrarRolesValidos(formValue.roles || []);
    
    const usuario: Usuario = {
      id: this.isEditMode() ? this.usuarioId || undefined : undefined,
      email: formValue.email,
      name: formValue.nombre,
      nombre: formValue.nombre,
      lastName: formValue.apellido,
      apellido: formValue.apellido,
      phone: formValue.phone,
      roles: rolesValidos,
      enabled: formValue.enabled,
      active: formValue.enabled
    };

    if (formValue.password) {
      usuario.password = formValue.password;
    }

    this.loading.set(true);
    this.usuariosService.guardarUsuario(usuario).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: this.isEditMode() ? 'Usuario actualizado correctamente' : 'Usuario creado correctamente',
          life: 3000
        });
        setTimeout(() => {
          this.router.navigate(['/usuarios']);
        }, 1500);
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error al guardar usuario:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al guardar el usuario',
          life: 3000
        });
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/usuarios']);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  cambiarClaveUsuario(): void {
    if (this.formCambioPassword.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos correctamente',
        life: 3000
      });
      return;
    }

    const usuarioCambioPassword: Usuario = {
      id: this.usuarioId || undefined,
      oldPassword: this.formCambioPassword.value.oldPassword,
      newPassword: this.formCambioPassword.value.newPassword,
      newRetypedPassword: this.formCambioPassword.value.newRetypedPassword
    };

    this.loading.set(true);
    this.securityService.actualizarPasswordUsuario(usuarioCambioPassword).subscribe({
      next: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Contraseña Actualizada',
          detail: 'La contraseña se ha actualizado correctamente',
          life: 3000
        });
        this.formCambioPassword.reset();
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error al cambiar contraseña:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Error al cambiar la contraseña',
          life: 3000
        });
      }
    });
  }

  forzarCambioPassword(): void {
    if (this.formForzarPassword.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos correctamente',
        life: 3000
      });
      return;
    }

    const passwordData = {
      forceNewPassword: this.formForzarPassword.value.forceNewPassword,
      forceRetypedPassword: this.formForzarPassword.value.forceRetypedPassword
    };

    this.loading.set(true);
    this.securityService.forzarCambioPassword(this.usuarioId!, passwordData).subscribe({
      next: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Contraseña Forzada',
          detail: 'La contraseña ha sido cambiada por el administrador',
          life: 3000
        });
        this.formForzarPassword.reset();
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error al forzar contraseña:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Error al forzar la contraseña',
          life: 3000
        });
      }
    });
  }

  isAdmin(): boolean {
    const userRoles = this.securityService.getUserRoles();
    return userRoles.includes('ROLE_ADMIN') || userRoles.includes('ROLE_SUPER_ADMIN') || userRoles.includes('ROLE_ADMIN_PUBLICIDAD');
  }
  
  togglePasswordVisibility(field: string): void {
    switch(field) {
      case 'password':
        this.showPassword.set(!this.showPassword());
        break;
      case 'confirmPassword':
        this.showConfirmPassword.set(!this.showConfirmPassword());
        break;
      case 'oldPassword':
        this.showOldPassword.set(!this.showOldPassword());
        break;
      case 'newPassword':
        this.showNewPassword.set(!this.showNewPassword());
        break;
      case 'newRetypedPassword':
        this.showNewRetypedPassword.set(!this.showNewRetypedPassword());
        break;
      case 'forceNewPassword':
        this.showForceNewPassword.set(!this.showForceNewPassword());
        break;
      case 'forceRetypedPassword':
        this.showForceRetypedPassword.set(!this.showForceRetypedPassword());
        break;
    }
  }

  canEditRoles(): boolean {
    // Solo admin y super_admin pueden cambiar roles
    return this.isAdmin();
  }

  get pageTitle(): string {
    return this.isEditMode() ? 'Editar Usuario' : 'Nuevo Usuario';
  }
}
